
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { ArrowNarrowRightIcon, PlayIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { FormEvent, useRef, useState } from 'react';
import styles from '@/components/sound-effects/SoundEffects.module.css';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import SoundExamples from '@/components/sound-effects/SoundExamples';
import { SOUND_EFFECT_CHARACTERS_PER_GENERATION } from '@/constants/common';
import { SOUND_EFFECT_DRAFT_KEY, SOUND_EFFECT_MAX_PROMPT_LENGTH, } from '@/constants/soundEffects';
import { useUser } from '@/utils/useUser';
const Footer = dynamic(() => import('@/components/layout/Footer'));
export default function AISoundEffectsGenerator() {
    const { t, lang } = useLocale('sound-effects-generator');
    const { user } = useUser();
    const router = useRouter();
    const [prompt, setPrompt] = useState('');
    const promptRef = useRef<HTMLTextAreaElement>(null);
    const destination = user
        ? '/sound-effects'
        : '/login?preset=sound#auth-sign-up';
    const useCases = [
        [
            '01',
            'Film & video',
            'Set the scene before a word is spoken.',
            'Rain on the window. A door down the hall. The details that make your edit feel alive.',
        ],
        [
            '02',
            'Games & worlds',
            'Give every action a sound.',
            'Invent a spell, a spaceship, or footsteps in a world that only exists in your imagination.',
        ],
        [
            '03',
            'Podcasts & stories',
            'Take your listeners somewhere.',
            'Build an atmosphere around your story with room tone, weather, and carefully placed effects.',
        ],
    ];
    const faqs = [
        [
            'What is Seed Audio 1.0?',
            'Seed Audio 1.0 is ByteDance’s audio creation model. Our sound effects tool uses it to turn descriptions into effects, foley, and layered ambience.',
        ],
        [
            'Can I choose the exact duration?',
            'Choose a target from 1 to 30 seconds. The model uses it as a guide, so the finished clip may be shorter or longer. Trim your download in an editor when exact timing matters.',
        ],
        [
            'Which audio formats can I download?',
            'Choose WAV for editing or MP3 for smaller files. Both are generated as 44.1 kHz stereo audio and saved in your sound history.',
        ],
        [
            'How much does generation use?',
            '{{count}} characters from your audio allowance per successful generation. The cost is shown before you generate. Listening to the samples on this page is free.',
        ],
        [
            'How do I write a good sound prompt?',
            'Describe what makes the sound, where it happens, and how it changes. Add details such as close or distant, dry or echoing, gentle or intense. Keep each prompt focused on one scene.',
        ],
    ];
    const cost = new Intl.NumberFormat(lang).format(SOUND_EFFECT_CHARACTERS_PER_GENERATION);
    function applyPrompt(value: string) {
        setPrompt(value);
        promptRef.current?.focus();
        document.getElementById('create-sound')?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
            block: 'center',
        });
    }
    function start(event: FormEvent) {
        event.preventDefault();
        try {
            localStorage.setItem(SOUND_EFFECT_DRAFT_KEY, prompt.trim());
        }
        catch {
            /* Storage is optional. */
        }
        void router.push(destination);
    }
    return (<div className={styles.landing} dir={lang === 'ar' ? 'rtl' : undefined}>
      <Seo templateTitle={t('AI Sound Effects Generator with Seed Audio 1.0')} description={t('Turn your ideas into sound effects, foley, and rich ambience with Seed Audio 1.0. Listen to real examples and create your own in WAV or MP3.')} image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(t('AI Sound Effects Generator'))}`} structuredData={false}/>
      <LandingPageNavigation />
      <main>
        <section className={styles.hero}>
          <div className={styles.heroCopy}>
            <div className={styles.heroEyebrow}>
              <span className={styles.kicker}>
                {t('AI Sound Effects Generator')}
              </span>
              <span className={styles.modelPill}>Seed Audio 1.0</span>
            </div>
            <h1>
              {t('You imagine it.')}
              <br />
              <em>{t('We make it sound.')}</em>
            </h1>
            <p className={styles.heroDescription}>
              {t('The hush before a storm. Footsteps in an empty hall. A world that hasn’t been heard yet. Turn a few words into the sound your story needs.')}
            </p>
            <div className={styles.heroActions}>
              <Link href={destination} className={styles.primaryLink}>
                {t('Create a sound effect')} <ArrowNarrowRightIcon />
              </Link>
              <a href='#listen' className={styles.textLink}>
                <PlayIcon />
                {t('Hear it for yourself')}
              </a>
            </div>
            <p className={styles.heroNote}>
              {t('Powered by ByteDance. Made for your imagination.')}
            </p>
          </div>
          <div id='listen' className={styles.heroDeck}>
            <SoundExamples onUsePrompt={applyPrompt}/>
          </div>
        </section>
        <div className={styles.featureStrip}>
          <span>{t('From text to texture')}</span>
          <span>{t('Foley & cinematic effects')}</span>
          <span>{t('Layered atmospheres')}</span>
          <span>{t('WAV & MP3 downloads')}</span>
        </div>

        <section className={styles.sceneSection}>
          <div className={styles.sectionIntro}>
            <span className={styles.kicker}>
              {t('Small details. Entire worlds.')}
            </span>
            <h2>
              {t('Make your audience')}
              <br />
              <em>{t('feel the scene.')}</em>
            </h2>
            <p>
              {t('Go beyond a single sound. Describe the foreground, the background, and the moment they meet.')}
            </p>
          </div>
          <div className={styles.useCases}>
            {useCases.map(([number, title, heading, description]) => (<article className={styles.useCase} key={number}>
                <span className={styles.useCaseNumber}>{number}</span>
                <div>
                  <span className={styles.kicker}>{t(title)}</span>
                  <h3>{t(heading)}</h3>
                  <p>{t(description)}</p>
                </div>
              </article>))}
          </div>
        </section>

        <section className={styles.createSection} id='create-sound'>
          <div className={styles.createIntro}>
            <span className={styles.kicker}>
              {t('Your words are the controls')}
            </span>
            <h2>
              {t('A whole scene.')}
              <br />
              <em>{t('In a few words.')}</em>
            </h2>
            <ol className={styles.steps}>
              <li>
                <strong>{t('Describe it.')}</strong>{' '}
                {t('Start with a sound, then add a setting and texture.')}
              </li>
              <li>
                <strong>{t('Shape it.')}</strong>{' '}
                {t('Choose a target length and WAV or MP3.')}
              </li>
              <li>
                <strong>{t('Make it yours.')}</strong>{' '}
                {t('Generate, listen, download, and drop it into your project.')}
              </li>
            </ol>
          </div>
          <form onSubmit={start} className={styles.landingForm}>
            <div className={styles.formTopline}>
              <span>{t('YOUR NEXT SOUND')}</span>
              <span aria-hidden='true'>↗</span>
            </div>
            <label htmlFor='landing-sound-prompt' className={styles.formLabel}>
              {t('What does your scene sound like?')}
            </label>
            <textarea ref={promptRef} id='landing-sound-prompt' rows={5} maxLength={SOUND_EFFECT_MAX_PROMPT_LENGTH} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t('Rain tapping on a café window. Cups clinking nearby. A distant rumble of thunder. No voices or music.')}/>
            <p>
              {t('Be specific. “Footsteps” becomes “slow boots on gravel, outdoors, close to the listener.”')}
            </p>
            <button type='submit' className={styles.primaryLink}>
              {t('Open the sound studio')} <ArrowNarrowRightIcon />
            </button>
            <small>
              {t('{{count}} audio characters per generation', { count: cost })}
            </small>
          </form>
        </section>

        <section className={styles.faqSection}>
          <div>
            <span className={styles.kicker}>{t('A few things to know')}</span>
            <h2>
              {t('Good questions.')}
              <br />
              <em>{t('Clear answers.')}</em>
            </h2>
          </div>
          <div className={styles.faqs}>
            {faqs.map(([question, answer]) => (<details key={question}>
                <summary>
                  {t(question)}
                  <span aria-hidden='true'>+</span>
                </summary>
                <p>{t(answer, { count: cost })}</p>
              </details>))}
          </div>
        </section>
        <section className={styles.finalCta}>
          <span className={styles.kicker}>
            {t('Less searching. More creating.')}
          </span>
          <h2>
            {t('Your next great sound')}
            <br />
            <em>{t('starts with a sentence.')}</em>
          </h2>
          <Link href={destination} className={styles.primaryLink}>
            {t('Create a sound effect')} <ArrowNarrowRightIcon />
          </Link>
          <div className={styles.related}>
            <Link href='/text-to-speech'>{t('Explore AI voices')} ↗</Link>
            <Link href='/ai-music-generator'>{t('Create AI music')} ↗</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>);
}

    async function __Next_Translate__getStaticProps__1a1a5dd1ab7__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-sound-effects-generator',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1a5dd1ab7__ as getStaticProps }
  